import { forwardRef } from "react"
import { motion } from "framer-motion"
import { DownloadIcon, Mail } from "lucide-react"

import { AboutMeButton } from "@/components/about-me-button"
import { Button } from "@/components/ui/button"
import { DownloadButton } from "@/components/download-button"
import ImageSlider from "../image-slider"

interface AboutProps {
    isInView: boolean
    fadeInComplete: boolean // Using fadeInComplete for unique keys if needed
    onContact: () => void
}

export const About = forwardRef<HTMLElement, AboutProps>(({ isInView, fadeInComplete, onContact }, ref) => {
    return (
        <section ref={ref} className="py-12 sm:py-20 bg-muted/30">
            <div className="container px-4 sm:px-6 max-w-7xl mx-auto">
                <motion.div
                    key={`about-${fadeInComplete}`}
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7 }}
                    className="max-w-3xl mx-auto text-center mb-10 sm:mb-16"
                >
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">About Me</h2>
                    <div className="h-1 w-16 sm:w-20 bg-primary mx-auto mb-6 sm:mb-8"></div>
                    <p className="text-base sm:text-lg text-muted-foreground">
                        As a full-stack developer, I specialize in creating dynamic and responsive web applications.
                        My expertise lies in HTML, CSS, JavaScript, and React, allowing me to build user-friendly
                        interfaces and with NodeJS, Golang, supporting me bringing the solutions that fits customers' demands.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 max-w-5xl mx-auto items-center">
                    <motion.div
                        key={`about-details-${fadeInComplete}`}
                        initial={{ opacity: 0, x: 50 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.7, delay: 0.4 }}
                        className="space-y-4 sm:space-y-6"
                    >
                        <div className="col-span-7 animate-fade-in-up text-center">
                            <h1 className="text-xl font-extrabold tracking-tighter leading-tight mb-8 text-emerald-500">
                                Warren Quoc Bui
                            </h1>
                            <div className="space-y-6 text-on-surface-variant text-lg max-w-2xl leading-relaxed">
                                <p>
                                    I am a dedicated software engineer focused on creating high-performance digital
                                    experiences. My approach balances technical precision with a business-driven knowledge, ensuring every
                                    logic works with customers' pleasure.
                                </p>
                                <p>
                                    With many years of experience across fintech, I bridge the gap between complex information systems and human-centric design.
                                </p>
                            </div>
                            <div className="mt-12 flex flex-wrap gap-2 justify-center">
                                <DownloadButton></DownloadButton>
                                <Button onClick={onContact} variant="outline" className="cursor-pointer px-8 py-4 rounded-md font-bold tracking-tight text-sm sm:text-base">
                                    Contact Me <Mail className="mr-2 h-4 w-4" />
                                </Button>
                                <AboutMeButton />
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        key={`about-image-${fadeInComplete}`}
                        initial={{ opacity: 0, x: -50 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.7, delay: 0.2 }}
                    >
                        <div className="relative max-w-sm sm:max-w-md mx-auto group">
                            <div>
                                <ImageSlider></ImageSlider>
                            </div>
                            <div
                                className="absolute -bottom-10 -left-20 bg-emerald-400 text-on-tertiary-fixed px-6 py-4 rounded-md shadow-xl hidden md:block">
                                <p className="text-xs font-bold tracking-widest uppercase text-white ">Currently based in</p>
                                <p className="text-xl font-bold text-blue-600 ">San Jose, CA, USA</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    )
})

About.displayName = "About"
